import { API_BASE_URL } from '@/api/config';
import { ApiError } from '@/auth/api';
import type {
  CompleteReservationInput,
  DevicePayment,
  PendingRefund,
  AdminPhoneModelItem,
  BasePriceInput,
  BranchItem,
  BranchOption,
  BrandItem,
  BrandOption,
  CreateBranchInput,
  DeviceFilters,
  FollowUpFilters,
  FollowUpView,
  UpdateBranchInput,
  CreateBrandInput,
  CreateInternalQuotationInput,
  CreatedInternalQuotation,
  CreatePhoneModelInput,
  ModelDiscountInput,
  PublicDeclaredField,
  QuotationConfig,
  QuotationSettings,
  UpdateBrandInput,
  PhoneModelOption,
  QuotationPhotoSummary,
  QuotationPhotoWithUrl,
  DevicePhoto,
  ContactSettings,
  PriceDisplaySettings,
  CreateQuotationRequestInput,
  CreatedQuotationRequest,
  NoteEntry,
  DeviceDetail,
  DeviceItem,
  QuotationFilters,
  QuotationRequestItem,
  ReceiveDeviceInput,
  UpdateDeviceInput,
  UpdateQuotationRequestInput,
  UpdatePhoneModelInput,
  ColorItem,
  CreateColorInput,
  DeviceModelOption,
  UpdateColorInput,
  WarrantySettingsInput,
  CreateDirectDeviceInput,
  CommercialDashboard,
  CommercialDashboardFilters,
} from './types';

export type PublicCatalogDevice = {
  id: string;
  brand: string;
  model: string;
  storageGb: number | null;
  color: string | null;
  batteryHealth: number | null;
  salePriceMinor: number | null;
  photos: string[];
};

export const publicCatalogParamNames = [
  'brand',
  'model',
  'color',
  'storage',
  'minPrice',
  'maxPrice',
  'sort',
  'page',
  'pageSize',
] as const;

export type PublicCatalogParamName = (typeof publicCatalogParamNames)[number];

/** Each option carries how many devices it would show with the other chosen filters. */
export type PublicCatalogFilterOptions = {
  brands: { id: string; name: string; count: number }[];
  models: { id: string; name: string; brandId: string; brandName: string; count: number }[];
  colors: { id: string; name: string; count: number }[];
  storages: { value: number; count: number }[];
};

export type PublicCatalogPage = {
  devices: PublicCatalogDevice[];
  total: number;
  page: number;
  pageSize: number;
  filterOptions: PublicCatalogFilterOptions;
};

/** The API rejected the filters themselves (a 400), as opposed to failing to answer. */
export class PublicCatalogQueryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PublicCatalogQueryError';
  }
}

/**
 * Only the catalogue's own parameters reach the API: a page URL may carry others (a campaign's
 * `utm_source`) that are not filters and must not turn into a rejected request.
 */
export async function getPublicDevices(
  params: URLSearchParams = new URLSearchParams(),
): Promise<PublicCatalogPage> {
  const query = new URLSearchParams();
  for (const name of publicCatalogParamNames) {
    for (const value of params.getAll(name)) query.append(name, value);
  }
  const search = query.toString();
  const response = await fetch(`${API_BASE_URL}/api/catalog/devices${search ? `?${search}` : ''}`);
  if (response.status === 400) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new PublicCatalogQueryError(body.error ?? 'Filtros inválidos');
  }
  return parseJsonResponse<PublicCatalogPage>(response);
}

/** What backs every published device: the warranty in force and PhoneTec's WhatsApp number. */
export type PublicCatalogSupport = {
  warranty: { durationMonths: number; conditions: string } | null;
  whatsappNumber: string | null;
};

/** Public. Read on every visit, so a change in the panel shows without a redeploy (CAT-3). */
export async function getCatalogSupport(): Promise<PublicCatalogSupport> {
  const response = await fetch(`${API_BASE_URL}/api/catalog/support`);
  return (await parseJsonResponse<{ support: PublicCatalogSupport }>(response)).support;
}

export type PublicCatalogDeviceDetail = PublicCatalogDevice & {
  characteristics: { label: string; value: string }[];
};

/**
 * One public device sheet. `null` means the device is not public (missing, pending, reserved,
 * sold or of an inactive brand): the API answers the same 404 for all of them on purpose.
 */
export async function getPublicDevice(id: string): Promise<PublicCatalogDeviceDetail | null> {
  const response = await fetch(`${API_BASE_URL}/api/catalog/devices/${encodeURIComponent(id)}`);
  if (response.status === 404) return null;
  return (await parseJsonResponse<{ device: PublicCatalogDeviceDetail }>(response)).device;
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = (await response.json()) as T | { error?: string };

  if (!response.ok) {
    const errorMessage =
      typeof data === 'object' && data && 'error' in data && data.error
        ? data.error
        : 'Request failed';
    // An Error still, so every caller that reads the message keeps working; the status is what
    // lets a page tell an expired session from a rejected request, and `details` carries what
    // the backend sent next to the message, such as the `fieldErrors` of a form.
    const details = Object.fromEntries(
      Object.entries((data ?? {}) as Record<string, unknown>).filter(([key]) => key !== 'error'),
    );
    throw new ApiError(response.status, errorMessage, details);
  }

  return data as T;
}

/** Public catalogue for the quotation form. No authentication: it is needed before anyone identifies themselves. */
export async function getPhoneModels(): Promise<PhoneModelOption[]> {
  const response = await fetch(`${API_BASE_URL}/api/phone-models`);
  const data = await parseJsonResponse<{ phoneModels: PhoneModelOption[] }>(response);
  return data.phoneModels;
}

/**
 * The questions the public form asks. No authentication, for the same reason as the model
 * catalogue: the form needs them before anyone identifies themselves.
 *
 * An empty list is a valid answer, not a failure: a PhoneTec that has configured no questions
 * asks none.
 */
export async function getDeclaredFields(): Promise<PublicDeclaredField[]> {
  const response = await fetch(`${API_BASE_URL}/api/quotation-fields`);
  const data = await parseJsonResponse<{ declaredFields: PublicDeclaredField[] }>(response);
  return data.declaredFields;
}

/// Quoting on the web needs a customer session (ADR 0008): the contact is the account's.
export async function createQuotationRequest(
  token: string,
  input: CreateQuotationRequestInput,
): Promise<{ quotation: CreatedQuotationRequest; message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/quotations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  return parseJsonResponse<{ quotation: CreatedQuotationRequest; message: string }>(response);
}

export async function getInternalQuotations(
  token: string,
  filters: QuotationFilters = {},
): Promise<QuotationRequestItem[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.search) params.set('search', filters.search);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  const queryString = params.toString();

  const response = await fetch(
    `${API_BASE_URL}/api/quotations${queryString ? `?${queryString}` : ''}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  const data = await parseJsonResponse<{ quotations: QuotationRequestItem[] }>(response);
  return data.quotations;
}

/** The commercial follow-up view (CRM-7, #111): stale quotations and active reservations. */
export async function getFollowUp(
  token: string,
  filters: FollowUpFilters = {},
): Promise<FollowUpView> {
  const params = new URLSearchParams();
  if (filters.group) params.set('group', filters.group);
  if (filters.branchId) params.set('branchId', filters.branchId);
  if (filters.quotationsPage) params.set('quotationsPage', String(filters.quotationsPage));
  if (filters.quotationsPageSize) {
    params.set('quotationsPageSize', String(filters.quotationsPageSize));
  }
  if (filters.reservationsPage) params.set('reservationsPage', String(filters.reservationsPage));
  if (filters.reservationsPageSize) {
    params.set('reservationsPageSize', String(filters.reservationsPageSize));
  }
  const queryString = params.toString();

  const response = await fetch(
    `${API_BASE_URL}/api/follow-up${queryString ? `?${queryString}` : ''}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );

  return parseJsonResponse<FollowUpView>(response);
}

export async function getInternalQuotation(
  token: string,
  quotationId: string,
): Promise<QuotationRequestItem> {
  const response = await fetch(`${API_BASE_URL}/api/quotations/${quotationId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await parseJsonResponse<{ quotation: QuotationRequestItem }>(response);
  return data.quotation;
}

export async function updateInternalQuotation(
  token: string,
  quotationId: string,
  input: UpdateQuotationRequestInput,
): Promise<QuotationRequestItem> {
  const response = await fetch(`${API_BASE_URL}/api/quotations/${quotationId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  const data = await parseJsonResponse<{ quotation: QuotationRequestItem }>(response);
  return data.quotation;
}

/**
 * Moves a counter quotation whose account is still pending to another account (#108). A 409
 * means it can no longer be moved (web quotation, account already active, or somebody else
 * corrected it first): the caller reloads the detail. `previousInvitationRevoked` says whether
 * the previous account, left without quotations, lost its live invitation.
 */
export async function changeQuotationAccount(
  token: string,
  quotationId: string,
  accountId: string,
): Promise<{ quotation: QuotationRequestItem; previousInvitationRevoked: boolean }> {
  const response = await fetch(`${API_BASE_URL}/api/quotations/${quotationId}/account`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ accountId }),
  });

  return parseJsonResponse(response);
}

export async function getInternalDevices(
  token: string,
  filters: DeviceFilters = {},
): Promise<DeviceItem[]> {
  const params = new URLSearchParams();
  if (filters.branchId) params.set('branchId', filters.branchId);
  if (filters.state) params.set('state', filters.state);
  if (filters.search) params.set('search', filters.search);
  const queryString = params.toString();

  const response = await fetch(
    `${API_BASE_URL}/api/devices${queryString ? `?${queryString}` : ''}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  const data = await parseJsonResponse<{ devices: DeviceItem[] }>(response);
  return data.devices;
}

export async function createDirectDevice(
  token: string,
  input: CreateDirectDeviceInput,
): Promise<DeviceDetail> {
  const response = await fetch(`${API_BASE_URL}/api/devices`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(input),
  });
  return (await parseJsonResponse<{ device: DeviceDetail }>(response)).device;
}

export async function getDeviceById(token: string, deviceId: string): Promise<DeviceDetail> {
  const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return (await parseJsonResponse<{ device: DeviceDetail }>(response)).device;
}

/**
 * Counter intake: the same request the public form creates, sent with a session.
 *
 * Answers with the internal detail rather than the narrow public shape, because whoever is at
 * the counter needs the reason an estimate could not be produced in order to have something to
 * say to the person standing there.
 */
export async function createInternalQuotation(
  token: string,
  input: CreateInternalQuotationInput,
): Promise<CreatedInternalQuotation> {
  const response = await fetch(`${API_BASE_URL}/api/quotations/internal`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  const data = await parseJsonResponse<{ quotation: CreatedInternalQuotation }>(response);
  return data.quotation;
}

/**
 * Reception: the phone declared in this request is now on the counter, and becomes a device.
 * The only way a device is created. Answers with the request, now carrying its device.
 */
export async function receiveQuotationDevice(
  token: string,
  quotationId: string,
  input: ReceiveDeviceInput,
): Promise<QuotationRequestItem> {
  const response = await fetch(`${API_BASE_URL}/api/quotations/${quotationId}/reception`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  const data = await parseJsonResponse<{ quotation: QuotationRequestItem }>(response);
  return data.quotation;
}

export async function updateInternalDevice(
  token: string,
  deviceId: string,
  input: UpdateDeviceInput,
): Promise<DeviceDetail> {
  const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  const data = await parseJsonResponse<{ device: DeviceDetail }>(response);
  return data.device;
}

/**
 * Uploads one photo against a just-created request.
 *
 * `Content-Type` is deliberately not set: the browser has to write it itself so the
 * multipart boundary matches the body it builds.
 */
export async function uploadQuotationPhoto(
  quotationId: string,
  uploadToken: string,
  file: File,
): Promise<QuotationPhotoSummary> {
  const body = new FormData();
  body.append('photo', file);

  const response = await fetch(`${API_BASE_URL}/api/quotations/${quotationId}/photos`, {
    method: 'POST',
    headers: { 'X-Upload-Token': uploadToken },
    body,
  });

  const data = await parseJsonResponse<{ photo: QuotationPhotoSummary }>(response);
  return data.photo;
}

export async function getInternalQuotationPhotos(
  token: string,
  quotationId: string,
): Promise<QuotationPhotoWithUrl[]> {
  const response = await fetch(`${API_BASE_URL}/api/quotations/${quotationId}/photos`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await parseJsonResponse<{ photos: QuotationPhotoWithUrl[] }>(response);
  return data.photos;
}

export async function getDevicePhotos(token: string, deviceId: string): Promise<DevicePhoto[]> {
  const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/photos`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return (await parseJsonResponse<{ photos: DevicePhoto[] }>(response)).photos;
}
export async function setDevicePublished(
  token: string,
  deviceId: string,
  publish: boolean,
): Promise<DeviceDetail> {
  const response = await fetch(
    `${API_BASE_URL}/api/devices/${deviceId}/${publish ? 'publish' : 'unpublish'}`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return (await parseJsonResponse<{ device: DeviceDetail }>(response)).device;
}
export async function sellInternalDevice(
  token: string,
  deviceId: string,
  saleDate: string,
  buyerId: string,
): Promise<DeviceDetail> {
  const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/sell`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ saleDate, buyerId }),
  });
  return (await parseJsonResponse<{ device: DeviceDetail }>(response)).device;
}

export async function revertInternalSale(
  token: string,
  deviceId: string,
  reason: string,
): Promise<DeviceDetail> {
  const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/sale/revert`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return (await parseJsonResponse<{ device: DeviceDetail }>(response)).device;
}

/** Cancel the device's active reservation with a reason (VEN-4). */
export async function cancelInternalReservation(
  token: string,
  deviceId: string,
  reservationId: string,
  reason: string,
): Promise<DeviceDetail> {
  const response = await fetch(
    `${API_BASE_URL}/api/devices/${deviceId}/reservations/${reservationId}/cancel`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    },
  );
  return (await parseJsonResponse<{ device: DeviceDetail }>(response)).device;
}

/** Concrete the sale of the device's active reservation (VEN-4). */
export async function completeInternalReservation(
  token: string,
  deviceId: string,
  reservationId: string,
  input: CompleteReservationInput,
): Promise<DeviceDetail> {
  const response = await fetch(
    `${API_BASE_URL}/api/devices/${deviceId}/reservations/${reservationId}/complete`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  return (await parseJsonResponse<{ device: DeviceDetail }>(response)).device;
}

export async function fetchDevicePayments(
  token: string,
  deviceId: string,
): Promise<DevicePayment[]> {
  const response = await fetch(
    `${API_BASE_URL}/api/payments?deviceId=${encodeURIComponent(deviceId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return (await parseJsonResponse<{ payments: DevicePayment[] }>(response)).payments;
}

export async function fetchPendingRefunds(token: string): Promise<PendingRefund[]> {
  const response = await fetch(`${API_BASE_URL}/api/payments/pending-refunds`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return (await parseJsonResponse<{ payments: PendingRefund[] }>(response)).payments;
}

export async function getCommercialDashboard(
  token: string,
  filters: CommercialDashboardFilters,
): Promise<CommercialDashboard> {
  const params = new URLSearchParams({ dateFrom: filters.dateFrom, dateTo: filters.dateTo });
  if (filters.branchId) params.set('branchId', filters.branchId);
  const response = await fetch(`${API_BASE_URL}/api/admin/dashboard?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseJsonResponse<CommercialDashboard>(response);
}

export async function registerPaymentRefund(
  token: string,
  paymentId: string,
  reason: string,
): Promise<DevicePayment> {
  const response = await fetch(`${API_BASE_URL}/api/payments/${paymentId}/refund`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return (await parseJsonResponse<{ payment: DevicePayment }>(response)).payment;
}

/** Reserve a published device for a buyer, for three days and without payment (VEN-2). */
export async function reserveInternalDevice(
  token: string,
  deviceId: string,
  buyerId: string,
): Promise<DeviceDetail> {
  const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/reservations`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ buyerId }),
  });
  return (await parseJsonResponse<{ device: DeviceDetail }>(response)).device;
}

export async function uploadDevicePhoto(
  token: string,
  deviceId: string,
  file: File,
): Promise<void> {
  const body = new FormData();
  body.append('photo', file);
  const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/photos`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  await parseJsonResponse(response);
}
export async function orderDevicePhotos(
  token: string,
  deviceId: string,
  photoIds: string[],
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/photos/order`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ photoIds }),
  });
  await parseJsonResponse(response);
}
export async function deleteDevicePhoto(
  token: string,
  deviceId: string,
  photoId: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/photos/${photoId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) await parseJsonResponse(response);
}

const ADMIN_CONFIG_URL = `${API_BASE_URL}/api/admin/quotation-config`;
const PRICE_DISPLAY_URL = `${API_BASE_URL}/api/quotation-fields/price-display`;

export async function getPriceDisplaySettings(): Promise<PriceDisplaySettings> {
  const response = await fetch(PRICE_DISPLAY_URL);
  return (await parseJsonResponse<{ priceDisplay: PriceDisplaySettings }>(response)).priceDisplay;
}

export async function updatePriceDisplaySettings(
  token: string,
  input: Pick<PriceDisplaySettings, 'exchangeRate' | 'showPricesInUyu'>,
): Promise<PriceDisplaySettings> {
  const response = await fetch(`${ADMIN_CONFIG_URL}/price-display`, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify(input),
  });
  return (await parseJsonResponse<{ priceDisplay: PriceDisplaySettings }>(response)).priceDisplay;
}
const ADMIN_BRANDS_URL = `${API_BASE_URL}/api/admin/brands`;
const ADMIN_BRANCHES_URL = `${API_BASE_URL}/api/admin/branches`;
const ADMIN_PHONE_MODELS_URL = `${API_BASE_URL}/api/admin/phone-models`;

/**
 * Brands available at device intake, active and inactive alike. Internal, and readable by
 * EMPLOYEE: PhoneTec receives phones from brands it no longer quotes online.
 */
export async function getBrandOptions(token: string): Promise<BrandOption[]> {
  const response = await fetch(`${API_BASE_URL}/api/brands`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await parseJsonResponse<{ brands: BrandOption[] }>(response);
  return data.brands;
}

/**
 * Branches to pick from when receiving a phone. Internal, and readable by EMPLOYEE: they are
 * the ones who take the device in. Inactive ones travel with their flag, so a device received
 * before a branch closed still names where it is.
 */
export async function getBranchOptions(token: string): Promise<BranchOption[]> {
  const response = await fetch(`${API_BASE_URL}/api/branches`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await parseJsonResponse<{ branches: BranchOption[] }>(response);
  return data.branches;
}

function adminHeaders(token: string) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export async function getBranches(token: string): Promise<BranchItem[]> {
  const response = await fetch(ADMIN_BRANCHES_URL, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await parseJsonResponse<{ branches: BranchItem[] }>(response);
  return data.branches;
}

export async function createBranch(token: string, input: CreateBranchInput): Promise<BranchItem> {
  const response = await fetch(ADMIN_BRANCHES_URL, {
    method: 'POST',
    headers: adminHeaders(token),
    body: JSON.stringify(input),
  });
  const data = await parseJsonResponse<{ branch: BranchItem }>(response);
  return data.branch;
}

export async function updateBranch(
  token: string,
  branchId: string,
  input: UpdateBranchInput,
): Promise<BranchItem> {
  const response = await fetch(`${ADMIN_BRANCHES_URL}/${branchId}`, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify(input),
  });
  const data = await parseJsonResponse<{ branch: BranchItem }>(response);
  return data.branch;
}

const CONTACT_SETTINGS_URL = `${API_BASE_URL}/api/contact-settings`;

/**
 * Records that the applicant was contacted. Not a status change: the request keeps its status,
 * this only leaves the actor and the date on file.
 */
export async function registerQuotationContact(
  token: string,
  quotationId: string,
  channel: string,
): Promise<NoteEntry> {
  const response = await fetch(`${API_BASE_URL}/api/quotations/${quotationId}/contacts`, {
    method: 'POST',
    headers: adminHeaders(token),
    body: JSON.stringify({ channel }),
  });
  const data = await parseJsonResponse<{ note: NoteEntry }>(response);
  return data.note;
}

export type NoteTarget = { type: 'quotation' | 'device'; id: string };

function notesUrl(target: NoteTarget) {
  const collection = target.type === 'quotation' ? 'quotations' : 'devices';
  return `${API_BASE_URL}/api/${collection}/${target.id}/notes`;
}

export async function getNotes(token: string, target: NoteTarget): Promise<NoteEntry[]> {
  const response = await fetch(notesUrl(target), {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await parseJsonResponse<{ notes: NoteEntry[] }>(response);
  return data.notes;
}

export async function addNote(token: string, target: NoteTarget, body: string): Promise<NoteEntry> {
  const response = await fetch(notesUrl(target), {
    method: 'POST',
    headers: adminHeaders(token),
    body: JSON.stringify({ body }),
  });
  const data = await parseJsonResponse<{ note: NoteEntry }>(response);
  return data.note;
}

export async function editNote(token: string, noteId: string, body: string): Promise<NoteEntry> {
  const response = await fetch(`${API_BASE_URL}/api/notes/${noteId}`, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify({ body }),
  });
  const data = await parseJsonResponse<{ note: NoteEntry }>(response);
  return data.note;
}

/** Null when no message has been configured yet: a valid answer, not a failure. */
export async function getContactSettings(token: string): Promise<ContactSettings | null> {
  const response = await fetch(CONTACT_SETTINGS_URL, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await parseJsonResponse<{ contactSettings: ContactSettings | null }>(response);
  return data.contactSettings;
}

/** ADMIN only, enforced by the backend. `null` clears the number. */
export async function updateWhatsAppNumber(
  token: string,
  whatsappNumber: string | null,
): Promise<ContactSettings> {
  const response = await fetch(`${CONTACT_SETTINGS_URL}/whatsapp-number`, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify({ whatsappNumber }),
  });
  const data = await parseJsonResponse<{ contactSettings: ContactSettings }>(response);
  return data.contactSettings;
}

/** PhoneTec's mailbox for web reservations (VEN-3). ADMIN only; `null` clears it. */
export async function updateReservationNotificationEmail(
  token: string,
  reservationNotificationEmail: string | null,
): Promise<ContactSettings> {
  const response = await fetch(`${CONTACT_SETTINGS_URL}/reservation-notification-email`, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify({ reservationNotificationEmail }),
  });
  const data = await parseJsonResponse<{ contactSettings: ContactSettings }>(response);
  return data.contactSettings;
}

/** ADMIN only, enforced by the backend. */
export async function updateContactSettings(
  token: string,
  whatsappMessageTemplate: string,
): Promise<ContactSettings> {
  const response = await fetch(CONTACT_SETTINGS_URL, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify({ whatsappMessageTemplate }),
  });
  const data = await parseJsonResponse<{ contactSettings: ContactSettings }>(response);
  return data.contactSettings;
}

/**
 * The models staff pick from when receiving or editing a device, each with the active colours
 * it comes in (#186). Internal, and readable by EMPLOYEE.
 */
export async function getPhoneModelOptions(token: string): Promise<DeviceModelOption[]> {
  const response = await fetch(`${API_BASE_URL}/api/phone-models/options`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await parseJsonResponse<{ phoneModels: DeviceModelOption[] }>(response);
  return data.phoneModels;
}

const ADMIN_COLORS_URL = `${API_BASE_URL}/api/admin/colors`;

/** The general list of colours, active and inactive (#186). */
export async function getColors(token: string): Promise<ColorItem[]> {
  const response = await fetch(ADMIN_COLORS_URL, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await parseJsonResponse<{ colors: ColorItem[] }>(response);
  return data.colors;
}

export async function createColor(token: string, input: CreateColorInput): Promise<ColorItem> {
  const response = await fetch(ADMIN_COLORS_URL, {
    method: 'POST',
    headers: adminHeaders(token),
    body: JSON.stringify(input),
  });
  const data = await parseJsonResponse<{ color: ColorItem }>(response);
  return data.color;
}

/** Renames a colour, activates it or deactivates it. */
export async function updateColor(
  token: string,
  colorId: string,
  input: UpdateColorInput,
): Promise<ColorItem> {
  const response = await fetch(`${ADMIN_COLORS_URL}/${colorId}`, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify(input),
  });
  const data = await parseJsonResponse<{ color: ColorItem }>(response);
  return data.color;
}

/** Every brand, active and inactive: the panel is where the inactive ones get reactivated. */
export async function getBrands(token: string): Promise<BrandItem[]> {
  const response = await fetch(ADMIN_BRANDS_URL, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await parseJsonResponse<{ brands: BrandItem[] }>(response);
  return data.brands;
}

export async function createBrand(token: string, input: CreateBrandInput): Promise<BrandItem> {
  const response = await fetch(ADMIN_BRANDS_URL, {
    method: 'POST',
    headers: adminHeaders(token),
    body: JSON.stringify(input),
  });
  const data = await parseJsonResponse<{ brand: BrandItem }>(response);
  return data.brand;
}

/** Renames a brand, activates it or deactivates it. */
export async function updateBrand(
  token: string,
  brandId: string,
  input: UpdateBrandInput,
): Promise<BrandItem> {
  const response = await fetch(`${ADMIN_BRANDS_URL}/${brandId}`, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify(input),
  });
  const data = await parseJsonResponse<{ brand: BrandItem }>(response);
  return data.brand;
}

/** Every catalogue model, including retired ones, for the ADMIN management screen. */
export async function getAdminPhoneModels(token: string): Promise<AdminPhoneModelItem[]> {
  const response = await fetch(ADMIN_PHONE_MODELS_URL, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await parseJsonResponse<{ phoneModels: AdminPhoneModelItem[] }>(response);
  return data.phoneModels;
}

export async function createPhoneModel(
  token: string,
  input: CreatePhoneModelInput,
): Promise<AdminPhoneModelItem> {
  const response = await fetch(ADMIN_PHONE_MODELS_URL, {
    method: 'POST',
    headers: adminHeaders(token),
    body: JSON.stringify(input),
  });
  const data = await parseJsonResponse<{ phoneModel: AdminPhoneModelItem }>(response);
  return data.phoneModel;
}

export async function updatePhoneModel(
  token: string,
  phoneModelId: string,
  input: UpdatePhoneModelInput,
): Promise<AdminPhoneModelItem> {
  const response = await fetch(`${ADMIN_PHONE_MODELS_URL}/${phoneModelId}`, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify(input),
  });
  const data = await parseJsonResponse<{ phoneModel: AdminPhoneModelItem }>(response);
  return data.phoneModel;
}

/**
 * The whole commercial configuration in one read: general settings, fixed questions and the
 * grid of models by answers.
 */
export async function getQuotationConfig(token: string): Promise<QuotationConfig> {
  const response = await fetch(ADMIN_CONFIG_URL, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseJsonResponse<QuotationConfig>(response);
}

/** Duration and conditions are one warranty, saved together with the authenticated actor. */
export async function updateWarrantySettings(
  token: string,
  input: WarrantySettingsInput,
): Promise<QuotationSettings> {
  const response = await fetch(`${ADMIN_CONFIG_URL}/warranty`, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify(input),
  });
  const data = await parseJsonResponse<{ settings: QuotationSettings }>(response);
  return data.settings;
}

/** One base price, for one model and one capacity. Saved on the spot. */
export async function setBasePrice(token: string, input: BasePriceInput): Promise<void> {
  const response = await fetch(`${ADMIN_CONFIG_URL}/base-prices`, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify(input),
  });
  await parseJsonResponse<unknown>(response);
}

/** One amount, for one model and one answer. No draft, no publish. */
export async function setModelDiscount(token: string, input: ModelDiscountInput): Promise<void> {
  const response = await fetch(`${ADMIN_CONFIG_URL}/discounts`, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify(input),
  });
  await parseJsonResponse<unknown>(response);
}

/** Stops quoting one model without taking it out of the catalogue. */
export async function setModelQuotable(
  token: string,
  phoneModelId: string,
  isQuotable: boolean,
): Promise<void> {
  const response = await fetch(`${ADMIN_CONFIG_URL}/models/${phoneModelId}/quotable`, {
    method: 'PUT',
    headers: adminHeaders(token),
    body: JSON.stringify({ isQuotable }),
  });
  await parseJsonResponse<unknown>(response);
}
